import React, { useState, useEffect, useRef } from 'react';
import './NavBar.css';

const NavBar = ({ currentSection }) => {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const dropdownRefs = useRef([]);

  const navItems = [
    {
      title: "About Us",
      id: "about",
      icon: "fas fa-info-circle",
      hasDropdown: true,
      submenu: [
        { title: "Vision & Mission", link: "/about-us/vision-mission", icon: "fas fa-eye" },
        { title: "Manufacturing", link: "/about-us/manufacturing", icon: "fas fa-industry" },
        { title: "Certifications", link: "/about-us/certifications", icon: "fas fa-certificate" },
      ]
    },
    {
      title: "Businesses",
      id: "businesses",
      icon: "fas fa-briefcase",
      hasDropdown: true,
      submenu: [
        { title: "Circles/Blanks", link: "/businesses/circles-blanks", icon: "fas fa-circle" },
        { title: "Cookware", link: "/businesses/utensils-cookwares", icon: "fas fa-utensils" },
        { title: "Sheets", link: "/businesses/sheets", icon: "fas fa-layer-group" },
        { title: "Aluminium Scrap", link: "/businesses/scraps", icon: "fas fa-recycle" },
      ]
    },
    {
      title: "Industries",
      id: "industries",
      icon: "fas fa-cogs",
      hasDropdown: false,
      link: "/industries-we-serve"
    },
    {
      title: "Brands",
      id: "brands",
      icon: "fas fa-gem",
      hasDropdown: true,
      submenu: [
        { title: "Amogh", link: "/brands/amogh", icon: "fas fa-star" },
      ]
    },
    {
      title: "Contact",
      id: "contact",
      icon: "fas fa-phone",
      hasDropdown: true,
      submenu: [
        { title: "Get In Touch", link: "/contact/overview", icon: "fas fa-handshake" },
        { title: "Send Enquiry", link: "/contact/enquiry-form", icon: "fas fa-envelope" },
        { title: "Visit Us", link: "/contact/information", icon: "fas fa-map-marker-alt" },
        { title: "Careers", link: "/contact/work-with-us", icon: "fas fa-users" },
      ]
    }
  ];

  // Handle scroll effects
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMenuOpen(false);
        setActiveDropdown(null);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown !== null) {
        const ref = dropdownRefs.current[activeDropdown];
        if (ref && !ref.contains(event.target)) {
          setActiveDropdown(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setActiveDropdown(null);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
  };

  const handleDropdownToggle = (index, event) => {
    if (isMobile) {
      event.preventDefault();
      setActiveDropdown(activeDropdown === index ? null : index);
    }
  };

  const handleMouseEnter = (index) => {
    if (!isMobile) {
      setActiveDropdown(index);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setActiveDropdown(null);
    }
  };

  return (
    <>
      <nav className={`bb-navbar ${scrolled ? 'bb-navbar--scrolled' : ''} ${isMenuOpen ? 'bb-navbar--menu-open' : ''}`}>
        <div className="bb-navbar__container">
          {/* Logo */}
          <div className="bb-navbar__brand">
            <a href="/" className="bb-navbar__logo" onClick={closeMenu}>
              <img 
                src="/images/bb-logo-transparent.png" 
                alt="B B Industry" 
                className="bb-navbar__logo-img"
              />
              <div className="bb-navbar__logo-text">
                <span className="bb-navbar__company-name">B B Industry</span>
                <span className="bb-navbar__tagline">Aluminum Excellence</span>
              </div>
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="bb-navbar__nav">
            {navItems.map((item, index) => (
              <div
                key={index}
                className="bb-navbar__item"
                ref={el => dropdownRefs.current[index] = el}
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
              >
                {item.hasDropdown ? (
                  <>
                    <button
                      className={`bb-navbar__link bb-navbar__link--dropdown ${
                        currentSection === item.id ? 'bb-navbar__link--active' : ''
                      }`}
                      onClick={(e) => handleDropdownToggle(index, e)}
                    >
                      <i className={`${item.icon} bb-navbar__icon`}></i>
                      <span>{item.title}</span>
                      <i className={`fas fa-chevron-down bb-navbar__arrow ${
                        activeDropdown === index ? 'bb-navbar__arrow--open' : ''
                      }`}></i>
                    </button>
                    
                    <div className={`bb-navbar__dropdown ${
                      activeDropdown === index ? 'bb-navbar__dropdown--open' : ''
                    }`}>
                      <div className="bb-navbar__dropdown-content">
                        {item.submenu.map((subItem, subIndex) => (
                          <a
                            key={subIndex}
                            href={subItem.link}
                            className="bb-navbar__dropdown-item"
                            onClick={closeMenu}
                          >
                            <i className={`${subItem.icon} bb-navbar__dropdown-icon`}></i>
                            <span>{subItem.title}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <a
                    href={item.link}
                    className={`bb-navbar__link ${
                      currentSection === item.id ? 'bb-navbar__link--active' : ''
                    }`}
                    onClick={closeMenu}
                  >
                    <i className={`${item.icon} bb-navbar__icon`}></i>
                    <span>{item.title}</span>
                  </a>
                )}
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="bb-navbar__cta">
            <a href="/contact/enquiry-form" className="bb-navbar__cta-btn" onClick={closeMenu}>
              <i className="fas fa-rocket bb-navbar__cta-icon"></i>
              <span>Get Quote</span>
            </a>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="bb-navbar__mobile-toggle"
            onClick={toggleMenu}
            aria-label="Toggle navigation menu"
            aria-expanded={isMenuOpen}
          >
            <span className={`bb-navbar__hamburger ${isMenuOpen ? 'bb-navbar__hamburger--open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`bb-navbar__mobile-menu ${isMenuOpen ? 'bb-navbar__mobile-menu--open' : ''}`}>
          <div className="bb-navbar__mobile-content">
            {/* Mobile Header */}
            <div className="bb-navbar__mobile-header">
              <div className="bb-navbar__mobile-logo">
                <img src="/images/bb-logo.png" alt="B B Industry" />
                <span>B B Industry</span>
              </div>
              <button 
                className="bb-navbar__mobile-close"
                onClick={closeMenu}
                aria-label="Close menu"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Mobile Navigation Items */}
            <div className="bb-navbar__mobile-nav">
              {navItems.map((item, index) => (
                <div key={index} className="bb-navbar__mobile-item">
                  {item.hasDropdown ? (
                    <>
                      <button
                        className={`bb-navbar__mobile-link bb-navbar__mobile-link--dropdown ${
                          activeDropdown === index ? 'bb-navbar__mobile-link--open' : ''
                        } ${currentSection === item.id ? 'bb-navbar__mobile-link--active' : ''}`}
                        onClick={(e) => handleDropdownToggle(index, e)}
                      >
                        <div className="bb-navbar__mobile-link-content">
                          <i className={`${item.icon} bb-navbar__mobile-icon`}></i>
                          <span>{item.title}</span>
                        </div>
                        <i className={`fas fa-chevron-down bb-navbar__mobile-arrow ${
                          activeDropdown === index ? 'bb-navbar__mobile-arrow--open' : ''
                        }`}></i>
                      </button>
                      
                      <div className={`bb-navbar__mobile-submenu ${
                        activeDropdown === index ? 'bb-navbar__mobile-submenu--open' : ''
                      }`}>
                        {item.submenu.map((subItem, subIndex) => (
                          <a
                            key={subIndex}
                            href={subItem.link}
                            className="bb-navbar__mobile-submenu-item"
                            onClick={closeMenu}
                          >
                            <i className={`${subItem.icon} bb-navbar__mobile-submenu-icon`}></i>
                            <span>{subItem.title}</span>
                          </a>
                        ))}
                      </div>
                    </>
                  ) : (
                    <a
                      href={item.link}
                      className={`bb-navbar__mobile-link ${
                        currentSection === item.id ? 'bb-navbar__mobile-link--active' : ''
                      }`}
                      onClick={closeMenu}
                    >
                      <div className="bb-navbar__mobile-link-content">
                        <i className={`${item.icon} bb-navbar__mobile-icon`}></i>
                        <span>{item.title}</span>
                      </div>
                    </a>
                  )}
                </div>
              ))}
            </div>

            {/* Mobile CTA */}
            <div className="bb-navbar__mobile-cta">
              <a href="/contact/enquiry-form" className="bb-navbar__mobile-cta-btn" onClick={closeMenu}>
                <i className="fas fa-rocket"></i>
                <span>Get Your Quote Today</span>
              </a>
              <div className="bb-navbar__mobile-contact">
                <p>Ready to discuss your project?</p>
                <a href="tel:+91-123-456-7890" className="bb-navbar__mobile-phone">
                  <i className="fas fa-phone"></i>
                  +91 123 456 7890
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Overlay */}
      {isMenuOpen && (
        <div 
          className="bb-navbar__overlay"
          onClick={closeMenu}
        ></div>
      )}
    </>
  );
};

export default NavBar;